import { Module } from 'vuex';
import { Store } from '../store';

export interface Locale {
  language: string;
  country: string;
}

export enum Stage {
  Test = 'TEST',
  Int = 'INT',
  Dev = 'DEV',
  PreProd = 'PPROD',
  Prod = 'PROD',
}

export interface ModelSelectionConfig {
  showDots: boolean;
}
export interface BatteryConfig {
  withStartSlider: boolean;
  hasBatteryMileage: boolean;
}
export interface ComponentConfig {
  modelSelection: ModelSelectionConfig;
  battery: BatteryConfig;
}

export interface BoundingBox {
  width: number;
  height: number;
}

export interface State {
  stage: Stage;
  locale: Locale;
  asSidebar: boolean;
  appBoundingBox: BoundingBox;
  modelSelectionConfig: ModelSelectionConfig;
  batteryConfig: BatteryConfig;
}

const initialState = (): State => {
  return {
    stage: (process.env.VUE_APP_STAGE as Stage | undefined) || Stage.Prod,
    locale: {
      country: 'DE',
      language: 'de',
    },
    asSidebar: false,
    appBoundingBox: {
      width: 0,
      height: 0,
    },
    modelSelectionConfig: {} as ModelSelectionConfig,
    batteryConfig: {} as BatteryConfig,
  };
};

export const config: Module<State, Store> = {
  namespaced: true,
  state: initialState(),
  getters: {
    showDebugToggleButton: (state): boolean => {
      const urlParams = new URLSearchParams(window.location.search);
      const debugQuery = urlParams.get('debug');
      return !!debugQuery || [Stage.Dev, Stage.Int, Stage.Test].includes(state.stage);
    },
    appBoundingBox: (state): BoundingBox => {
      return state.appBoundingBox;
    },
    asSidebar: (state): boolean => {
      return state.asSidebar;
    },
    showModelSelectionDots: (state): boolean => {
      return state.modelSelectionConfig.showDots;
    },
    hasBatteryStartSlider: (state): boolean => {
      return state.batteryConfig.withStartSlider;
    },
    hasBatteryMileage: (state): boolean => {
      return state.batteryConfig.hasBatteryMileage;
    },
  },
  actions: {
    setStage: ({ commit }, stage: string) => {
      if (Object.values<string>(Stage).includes(stage)) {
        commit('setStage', stage);
      }
    },
    setLocale: ({ commit }, locale: Locale) => {
      commit('setLocale', locale);
    },
    setAsSidebar: ({ commit }, asSidebar: boolean) => {
      commit('setAsSidebar', asSidebar);
    },
    setAppBoundingBox: ({ commit }, appBoundingBox: BoundingBox) => {
      if (appBoundingBox.width && appBoundingBox.height) {
        commit('setAppBoundingBox', appBoundingBox);
      }
    },
    setComponentConfig: ({ commit, dispatch }, config: ComponentConfig) => {
      commit('setModelSelectionConfig', config.modelSelection);
      commit('setBatteryConfig', config.battery);
      if (config.battery.withStartSlider) {
        dispatch('chargingData/setBatteryChargingStart', 10, { root: true });
      }
    },
  },
  mutations: {
    setStage: (state, stage: Stage) => {
      state.stage = stage;
    },
    setLocale: (state, locale: Locale) => {
      state.locale = locale;
    },
    setAsSidebar: (state, asSidebar: boolean) => {
      state.asSidebar = asSidebar;
    },
    setAppBoundingBox: (state, appBoundingBox: BoundingBox) => {
      state.appBoundingBox = appBoundingBox;
    },
    setModelSelectionConfig: (state, modelSelectionConfig: ModelSelectionConfig) => {
      state.modelSelectionConfig = modelSelectionConfig;
    },
    setBatteryConfig: (state, batteryConfig: BatteryConfig) => {
      state.batteryConfig = batteryConfig;
    },
  },
};
